/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { HistoryModel } from '@app/models/history.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { HistoryListService } from '@app/services/api-services/history/history-list.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { Subscription } from 'rxjs';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class HistoryService extends ApiBaseService {
  private _history: HistoryModel;
  protected _endpoint = 'history';
  private eventSubscription: Subscription;

  constructor(
    http: HttpClient,
    authUserService: AuthUserService,
    private historyListService: HistoryListService
  ) {
    super(http, authUserService);
  }

  get history(): HistoryModel {
    return this._history;
  }

  set history(value: HistoryModel) {
    this._history = value;
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getHistoryFromListById
   *
   * : Get a HistoryModel based on the id of the History within the model by checking the list
   *
   * @param id of type string
   *
   * @returns HistoryModel: gets the History with the id that meets the query
   */
  getHistoryFromListById(historyId: string): void {
    this.history = this.historyListService.getSingleHistoryByEventId(historyId);
    if (this.history) {
      this.dataSubject.next(this.history);
    } else {
      this.getHistoryById(historyId);
    }
  }

  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  getHistoryById
   *
   * : Get an History based on the id by filtering the API query
   *
   * @param id of type string
   *
   * @returns Void: gets the History with the id that meets the query
   */
  public getHistoryById(historyId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = {
      field: 'history_id',
      operator: 'eq',
      value: historyId,
    };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }

  public addHistoryElement(data: any) {
    this.insertData({}, data);
  }

  /**
   * Warren Dowey & Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type HistoryModel
   *
   * @returns Void: handle a get request from the postgres DB
   */
  protected handleData(data: HistoryModel[]): void {
    this.history = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleInsert
   *
   * : handles the insert of a new History into the list
   *
   * @param data of type HistoryModel
   *
   * @returns Void: inserts a new History into the lsit
   */
  protected handleInsert(data: HistoryModel[]): void {
    if (data) {
      this.history = data[0];
      this.historyListService.addToList(data[0]);
    }
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleUpdate
   *
   * : handles the update of an History in the list
   *
   * @param data of type HistoryModel
   *
   * @returns Void: update the lsit
   */
  protected handleUpdate(data: HistoryModel[]): void {
    this.history = data[0];
    this.historyListService.updateInList(data[0]);
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey & Ryan Duffy
   *
   *  handleDelete
   *
   * : deletes an History from the list
   *
   * @param data of type HistoryModel
   *
   * @returns Void: deletes a History from the list
   */
  protected handleDelete(data: HistoryModel[]): void {
    this.historyListService.removeFromList(data[0]);
    super.handleDelete(data);
  }

  ngOnDestroy(): void {
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }
}
